import type { BaseQueryApi } from '@reduxjs/toolkit/query';
import type { AssetMetricPoint } from '@src/providers/CoinMetricsApiProvider';
import type {
  AssetMetricsParams,
  CoinMetricsDataResponse,
} from '@src/providers/CoinMetricsApiProvider/coinMetrics.types';
import { runCoinMetricsQuery } from '@src/providers/CoinMetricsApiProvider/coinMetricsEndpoints';
import { baseRestApi } from '@src/utils/rtkQuery/baseRestApi';
import { logger } from '@talos/kyoko';
import { chunk } from 'lodash-es';
import {
  fetchAssetMetricsInBatches,
  groupPointsByAsset,
  mergeBatchResultsIntoPointsByAssetId,
} from './assetLeaderboardMetrics';
import {
  advanceAssetLeaderboardSnapshotProgress,
  resetAssetLeaderboardSnapshotProgress,
} from './assetLeaderboardSnapshotProgress';
import { ASSET_LEADERBOARD_BATCH_CONCURRENCY, ASSET_LEADERBOARD_BATCH_SIZE } from './tokens';

export interface AssetLeaderboardSnapshotArgs {
  /**
   * Sorted, comma-joined CoinMetrics asset ids — both the cache key (see `serializeQueryArgs` below) and
   * the sole source of the ids `queryFn` fetches (split back out inside it). Two independently-resolved
   * universes that happen to differ only in iteration order still land on the same cache entry and share
   * one fetch/poll cycle rather than silently doubling requests. Deliberately the only id-bearing field on
   * this type: a parallel `cmAssetIds` array the caller must keep in sync with `signature` would let a
   * caller bug fetch a universe that doesn't match the cache entry it's stored under.
   */
  signature: string;
  metrics: string;
  frequency: string;
  limitPerAsset: number;
}

async function fetchAssetMetricsPage(
  params: AssetMetricsParams,
  api: BaseQueryApi
): Promise<CoinMetricsDataResponse<AssetMetricPoint>> {
  // Goes straight through the shared retry/auth-invalidation wrapper rather than another endpoint's
  // `initiate` — each id batch would otherwise become its own redundant RTK Query cache entry
  // duplicating the same points this endpoint already merges and caches as one snapshot.
  const result = await runCoinMetricsQuery<CoinMetricsDataResponse<AssetMetricPoint>>(
    { url: '/timeseries/asset-metrics', params, tier: 'pro', maxPages: 20 },
    api
  );
  if ('error' in result) {
    throw new Error(result.error.message);
  }
  return result.data;
}

/**
 * Shared, cross-panel Asset Leaderboard snapshot: batches `cmAssetIds` into bounded-concurrency
 * `/timeseries/asset-metrics` requests and merges them into one flat point list, cached by RTK Query
 * under `signature` (see `serializeQueryArgs`) so every mounted panel resolving the same universe
 * subscribes to one fetch/poll cycle instead of each running its own.
 *
 * Carry-over across poll cycles (a failed batch keeps its assets' last good points rather than blanking
 * them) is read from this endpoint's own previous cached result via `select`, since — unlike the
 * per-panel `useRef` this replaces — there's no component instance to hold it between cycles; the cache
 * entry itself is the only thing that outlives one fetch. Never resolves to `{ error }`: a cycle where
 * every batch fails still returns whatever carried-over points exist (possibly none), so `isError` stays
 * a caller-side "no usable data at all" check rather than replacing rows with an error screen the moment
 * one poll has a bad request.
 */
export const assetLeaderboardSnapshotApi = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    getAssetLeaderboardSnapshot: builder.query<AssetMetricPoint[], AssetLeaderboardSnapshotArgs>({
      queryFn: async (args, api) => {
        const getPreviousPoints = (): readonly AssetMetricPoint[] =>
          assetLeaderboardSnapshotApi.endpoints.getAssetLeaderboardSnapshot.select(args)(
            // The generated selector expects the full app root state; this endpoint only ever reads its
            // own slice of it, so the precise root-state type (owned by the app's store setup, not this
            // RTK Query slice) isn't worth threading through here.
            api.getState() as Parameters<
              ReturnType<typeof assetLeaderboardSnapshotApi.endpoints.getAssetLeaderboardSnapshot.select>
            >[0]
          ).data ?? [];

        try {
          const pointsByAssetId = groupPointsByAsset(getPreviousPoints());

          const cmAssetIds = args.signature === '' ? [] : args.signature.split(',');
          const batches = chunk(cmAssetIds, ASSET_LEADERBOARD_BATCH_SIZE);
          resetAssetLeaderboardSnapshotProgress(args.signature, cmAssetIds.length);

          const batchResults = await fetchAssetMetricsInBatches(
            params => fetchAssetMetricsPage(params, api),
            batches,
            { metrics: args.metrics, frequency: args.frequency, limit_per_asset: args.limitPerAsset },
            ASSET_LEADERBOARD_BATCH_CONCURRENCY,
            batch => advanceAssetLeaderboardSnapshotProgress(args.signature, batch.length)
          );

          mergeBatchResultsIntoPointsByAssetId(pointsByAssetId, batchResults);
          return { data: Array.from(pointsByAssetId.values()).flat() };
        } catch (error) {
          // Per-batch failures are already isolated inside fetchAssetMetricsInBatches (they resolve to
          // `{ points: null }`, never rejecting) — this only catches a genuinely unexpected failure. Never
          // resolve to `{ error }` here either: keep whatever carried-over data exists rather than let a
          // bug in this queryFn replace the grid with an error screen — `isError` is a caller-side "no
          // usable data at all" check, not a reflection of this queryFn failing.
          logger.error(new Error('[asset-leaderboard] snapshot fetch failed unexpectedly'), {
            extra: { error: String(error) },
          });
          return { data: Array.from(getPreviousPoints()) };
        }
      },
      // Explicit allowlist (rather than the full `queryArgs` object) so the key stays exactly these four
      // fields even if `AssetLeaderboardSnapshotArgs` grows more later. Returning an object (not a string)
      // lets RTK Query's default serializer re-add the endpoint-name prefix, so this can't collide with an
      // unrelated endpoint's key (see `wsEndpointBuilder.ts`'s equivalent note on the same pitfall).
      serializeQueryArgs: ({ queryArgs }) => ({
        signature: queryArgs.signature,
        metrics: queryArgs.metrics,
        frequency: queryArgs.frequency,
        limitPerAsset: queryArgs.limitPerAsset,
      }),
      // So `CoinMetricsCredentialsSyncer` can force this cache entry to refetch when the org's API
      // key changes, same as every endpoint in `coinMetricsEndpoints.ts` — without this, a snapshot
      // cached under a bad/rotated key would only recover once its universe signature happens to
      // change or the hourly poll ticks over, not immediately when the key is fixed.
      providesTags: ['CoinMetrics'],
    }),
  }),
});

export const { useGetAssetLeaderboardSnapshotQuery } = assetLeaderboardSnapshotApi;
